/* eslint-disable react/jsx-props-no-spreading */
import { DataFieldListItem } from "@iventis/domain-model/model/dataFieldListItem";
import { EventBlockSystemDataFieldType } from "@iventis/domain-model/model/eventBlockSystemDataFieldType";
import { ScheduleConfig } from "@iventis/domain-model/model/scheduleConfig";
import { PopoverProps, Popover } from "@mui/material";
import { formGap, popoverAnchorGap, popoverPadding } from "@iventis/styles/src/atomic-rules";
import React, { FunctionComponent, useEffect, useMemo, useRef, useState } from "react";
import { styled } from "@iventis/styles";
import { Subtitle } from "@iventis/styles/src/components/texts";
import { Content } from "@iventis/translations/content/typed-content";
import { EventBlockTypeDataFields } from "@iventis/domain-model/model/eventBlockTypeDataFields";
import { LoadingComponent } from "@iventis/components";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { getDataFieldListItems } from "@iventis/utilities";
import { scheduleApi } from "@iventis/api/src/api";
import { renderOptionWithBox } from "./formik-components/datafield-input";

export const KeyPopoverComponent: FunctionComponent<{
    keyAnchor: HTMLAnchorElement;
    setKeyAnchor: (anchor: HTMLAnchorElement) => void;
    config: ScheduleConfig;
    showEventUnits: boolean;
}> = ({ keyAnchor, setKeyAnchor, config, showEventUnits }) => {
    const translate = useIventisTranslate();

    const calendarLevel = showEventUnits ? 2 : 1;

    const styleDataField = useMemo(() => {
        let df: EventBlockTypeDataFields;
        df = config.eventBlockTypes.find((type) => type.calendarLevel === calendarLevel).dataFields.find((df) => df.systemDataFieldType === EventBlockSystemDataFieldType.Style);
        if (df == null && showEventUnits) {
            // If we're displaying event units and event units have no style data field, just use the sessions' data field instead
            df = config.eventBlockTypes.find((type) => type.calendarLevel === 1).dataFields.find((df) => df.systemDataFieldType === EventBlockSystemDataFieldType.Style);
        }
        return df;
    }, []);

    const popoverProps = useMemo<PopoverProps>(
        () => ({
            open: keyAnchor != null,
            onClose: () => setKeyAnchor(null),
            anchorEl: keyAnchor,
            anchorOrigin: { vertical: "bottom", horizontal: "left" },
            transformOrigin: { vertical: "bottom", horizontal: "right" },
            style: { transform: `translateX(-${popoverAnchorGap})` },
        }),
        [keyAnchor]
    );

    const [state, setState] = useState<"loading" | "idle">("loading");

    const items = useRef<DataFieldListItem[]>([]);

    useEffect(() => {
        if (styleDataField != null) {
            getDataFieldListItems(scheduleApi, styleDataField.dataFieldId, []).then((listItems) => {
                items.current = listItems;
                setState("idle");
            });
        }
    }, []);

    return (
        <>
            {state === "idle" && (
                <Popover transitionDuration={0} {...popoverProps}>
                    <StyledContent>
                        <Subtitle id="key-title">{translate(Content.schedule2.key.title)}</Subtitle>
                        {items.current.map((item) => renderOptionWithBox({ className: "key-items" }, item))}
                    </StyledContent>
                </Popover>
            )}
            {state === "loading" && (
                <Popover {...popoverProps}>
                    {/* Creates a small box to show a loading spinner */}
                    <div style={{ position: "relative", width: "50px", height: "50px" }}>
                        <LoadingComponent size={30} />
                    </div>
                </Popover>
            )}
        </>
    );
};

const StyledContent = styled.div`
    padding: ${popoverPadding.standard};
    display: flex;
    flex-direction: column;
    gap: ${formGap};
    .key-items {
        display: flex;
        align-items: center;
    }
`;
